/**
 * Impersonation Hooks
 * Allows super_admin users to view the system as another user
 *
 * Uses Better Auth's adminClient plugin which properly manages:
 * - Session cookies for the impersonated user
 * - admin_session cookie to backup the admin's original session
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from '@/lib/auth-client';

/**
 * Hook to start impersonating a user
 * Only available to super_admin role
 */
export function useImpersonateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      // Use Better Auth's built-in method - handles cookies properly
      const result = await admin.impersonateUser({ userId });
      if (result.error) {
        throw new Error(result.error.message || 'Failed to impersonate user');
      }
      return result.data;
    },
    onSuccess: () => {
      // Refresh current user to get impersonated session
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      // Redirect to dashboard to view as impersonated user
      window.location.href = '/';
    },
  });
}

/**
 * Hook to stop impersonating and return to admin session
 */
export function useStopImpersonating() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Use Better Auth's built-in method - restores admin session from backup cookie
      const result = await admin.stopImpersonating();
      if (result.error) {
        throw new Error(result.error.message || 'Failed to stop impersonating');
      }
      return result.data;
    },
    onSuccess: () => {
      // Refresh current user to get back to admin session
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      // Redirect to admin users page
      window.location.href = '/users';
    },
  });
}
